/**
 * SwipeableCard — touch-enabled swipe gestures for cards.
 * Supports swipe-left (delete), swipe-right (complete), and a 3-dot menu.
 * Desktop: shows 3-dot menu only. Mobile: supports touch gestures.
 */
import { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useSwipe } from '../../hooks/useSwipe';
import { Navigation, Copy } from 'lucide-react';

interface SwipeableCardProps {
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  onDirections?: () => void;
  onShare?: () => void;
  onClone?: (() => void) | null;
  isCompleted?: boolean;
  children: React.ReactNode;
  threshold?: number;
}

export function SwipeableCard({ onSwipeLeft, onSwipeRight, onDirections, onShare, onClone, isCompleted, children, threshold = 60 }: SwipeableCardProps) {
  const { offset, touchAction, onTouchStart, onTouchMove, onTouchEnd, reset } = useSwipe({
    onSwipeLeft,
    onSwipeRight,
    threshold,
  });
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const [menuTop, setMenuTop] = useState<number | null>(null);
  const [menuRight, setMenuRight] = useState<number | null>(null);

  useEffect(() => {
    if (offset !== 0) {
      const close = () => reset();
      document.addEventListener('click', close);
      return () => { document.removeEventListener('click', close); };
    }
  }, [offset, reset]);

  useEffect(() => {
    if (!menuOpen || !menuButtonRef.current) return;
    const rect = menuButtonRef.current.getBoundingClientRect();
    setMenuTop(rect.bottom);
    setMenuRight(window.innerWidth - rect.right);
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && menuButtonRef.current &&
        !menuRef.current.contains(e.target as Node) &&
        !menuButtonRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => { document.removeEventListener('click', handleClickOutside); };
  }, [menuOpen]);

  const handleMenuToggle = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setMenuOpen(prev => !prev);
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _handleShare = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setMenuOpen(false);
    onShare?.();
  }, [onShare]);

  const canInteract = Math.abs(offset) > 20;

  return (
    <div
      ref={wrapperRef}
      className="relative overflow-hidden"
      style={{ touchAction }}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {/* Action buttons behind the card */}
      <div className="absolute inset-0 flex">
        {onSwipeLeft && (
          <div
            className={`flex items-center px-4 bg-red-500 dark:bg-red-600 rounded-l-xl h-full flex-shrink-0 ${canInteract ? '' : 'pointer-events-none'
              }`}
          >
            <button
              onClick={(e) => { e.stopPropagation(); onSwipeLeft(); reset(); }}
              className="text-white p-1"
              aria-label="Delete"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        )}
        {onSwipeRight && (
          <div
            className={`flex items-center px-4 bg-blue-500 dark:bg-blue-600 rounded-r-xl h-full flex-shrink-0 ml-auto ${canInteract ? '' : 'pointer-events-none'
              }`}
          >
            <button
              onClick={(e) => { e.stopPropagation(); onSwipeRight(); reset(); }}
              className="text-white p-1"
              aria-label="Mark as completed"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* Sliding card content */}
      <div
        className="relative z-10"
        style={{
          transform: offset !== 0 ? `translateX(${offset}px)` : undefined,
          transition: 'transform 0.3s ease-out',
        }}
      >
        <div className={`bg-white dark:bg-surface-dark-card rounded-xl shadow-card overflow-hidden dark:ring-1 dark:ring-white/[0.06]${onDirections ? ' pr-10' : ''}${(onSwipeLeft || onSwipeRight || onShare || onClone) ? ' md:pr-10' : onDirections ? ' md:pr-0' : ''}`}>
          {children}
        </div>
        {/* Mobile-only directions button */}
        {onDirections && (
          <button
            onClick={(e) => { e.stopPropagation(); onDirections(); }}
            className={`absolute top-1/2 -translate-y-1/2 right-1.5 z-20 p-1.5 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors block md:hidden
              ${canInteract ? 'opacity-0 pointer-events-none' : 'opacity-100'}
            `}
            aria-label="Get directions"
          >
            <Navigation className="w-4 h-4" />
          </button>
        )}
        {/* 3-dot menu button — desktop only */}
        {(onSwipeLeft || onSwipeRight || onShare || onClone) && (
          <div className="absolute top-2 right-2 z-20 hidden md:block">
            <button
              ref={menuButtonRef}
              onClick={handleMenuToggle}
              className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              aria-label="Actions"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01" />
              </svg>
            </button>
            {menuOpen && menuTop != null && menuRight != null && createPortal(
              <div
                ref={menuRef}
                style={{ top: menuTop, right: menuRight }}
                className="fixed w-36 origin-top-right bg-white dark:bg-surface-dark-card rounded-lg shadow-elevated border border-gray-200 dark:border-gray-700 py-0.5 z-50"
              >
                {onClone && (
                  <button
                    onClick={() => { onClone(); setMenuOpen(false); }}
                    className="w-full flex items-center gap-2 px-2 py-1.5 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    <Copy className="w-3.5 h-3.5 shrink-0" />
                    Clone
                  </button>
                )}
                {onSwipeLeft && (
                  <button
                    onClick={() => { onSwipeLeft(); setMenuOpen(false); }}
                    className="w-full flex items-center gap-2 px-2 py-1.5 text-xs text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                  >
                    <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Delete
                  </button>
                )}
                {onSwipeRight && (
                  <button
                    onClick={() => { onSwipeRight(); setMenuOpen(false); }}
                    className="w-full flex items-center gap-2 px-2 py-1.5 text-xs text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                  >
                    <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    {isCompleted ? 'Undo' : 'Done'}
                  </button>
                )}
                {onDirections && (
                  <button
                    onClick={() => { onDirections(); setMenuOpen(false); }}
                    className="w-full flex items-center gap-2 px-2 py-1.5 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    <Navigation className="w-3.5 h-3.5 shrink-0" />
                    Directions
                  </button>
                )}
              </div>,
              document.body
            )}
          </div>
        )}
      </div>
    </div>
  );
}
